import React, { useEffect, useState } from 'react';
import { X, Loader2, Target, Layers, Users, Calculator, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import authedFetch from '../../lib/authedFetch';
import secureStorage from '../../lib/secureStorage';
import logger from '../../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Inline Create Case — lets a user spin up a fully-calculated
 * business case from inside BusinessCaseBuilder without bouncing
 * through Solution Finder. Flow:
 *   1) pick a KPI                 → enables step 2
 *   2) pick ≥1 intervention       → enables step 3
 *   3) confirm audience/modality  → "Calculate & Save"
 * which chains POST /solution-finder/calculate → POST /business-case-builder/save
 * and hands the new case id back to the parent so it can load it.
 */
export function InlineCreateCaseModal({ open, onClose, onCreated }) {
  const [step, setStep] = useState(0);
  const [kpis, setKpis] = useState([]);
  const [interventions, setInterventions] = useState([]);
  const [modalities, setModalities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState(null);

  const [selectedKPI, setSelectedKPI] = useState(null);
  // Map of intervention_id → { modality_id, target_audience_size }
  const [selected, setSelected] = useState({});
  const [timeHorizon, setTimeHorizon] = useState(12);

  // Reset state each time the modal opens
  useEffect(() => {
    if (!open) return;
    setStep(0);
    setSelectedKPI(null);
    setSelected({});
    setError(null);
    setTimeHorizon(12);
  }, [open]);

  // Fetch KPI catalog + modality catalog once the modal opens
  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const [kRes, mRes] = await Promise.all([
          authedFetch(`${API_URL}/api/solution-finder/kpis`),
          authedFetch(`${API_URL}/api/solution-finder/modalities`),
        ]);
        const kData = await kRes.json();
        const mData = await mRes.json();
        setKpis(kData.kpis || []);
        setModalities(mData.modalities || []);
      } catch (e) {
        logger.error('inline-create fetch catalogs failed:', e);
        setError('Could not load KPI catalog');
      }
      setLoading(false);
    })();
  }, [open]);

  // Refetch aligned interventions whenever KPI changes
  useEffect(() => {
    if (!selectedKPI) {
      setInterventions([]);
      return;
    }
    (async () => {
      try {
        const r = await authedFetch(
          `${API_URL}/api/solution-finder/kpis/${selectedKPI.id}/interventions`
        );
        const d = await r.json();
        setInterventions(d.interventions || []);
      } catch (e) {
        logger.error('inline-create fetch interventions failed:', e);
      }
    })();
  }, [selectedKPI]);

  const handleKPI = (kpi) => {
    setSelectedKPI(kpi);
    setSelected({}); // reset interventions when KPI changes
    setStep(1);
  };

  const toggleIntervention = (iv) => {
    setSelected(prev => {
      const next = { ...prev };
      if (next[iv.id]) {
        delete next[iv.id];
      } else {
        next[iv.id] = {
          intervention_id: iv.id,
          modality_id: modalities[0]?.id || 'blended',
          target_audience_size: 100,
        };
      }
      return next;
    });
  };

  const updateConfig = (iv_id, patch) => {
    setSelected(prev => ({
      ...prev,
      [iv_id]: { ...prev[iv_id], ...patch },
    }));
  };

  const canProceedFromStep1 = Object.keys(selected).length > 0;
  const canSubmit =
    canProceedFromStep1 &&
    Object.values(selected).every(c => c.modality_id && c.target_audience_size > 0);

  const handleCalculateAndSave = async () => {
    if (!canSubmit) return;
    setCalculating(true);
    setError(null);
    const token = secureStorage.getItem('session_token');
    try {
      // 1) Calculate ROI
      const calcRes = await authedFetch(`${API_URL}/api/solution-finder/calculate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          kpi_id: selectedKPI.id,
          interventions: Object.values(selected),
          time_horizon_months: timeHorizon,
        }),
      });
      if (!calcRes.ok) throw new Error('calc failed');
      const roi = await calcRes.json();

      // 2) Save as a business case (non-draft)
      const title = `${selectedKPI.name} — Inline Draft ${new Date()
        .toISOString()
        .slice(0, 10)}`;
      const saveRes = await authedFetch(`${API_URL}/api/business-case-builder/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          title,
          description: `Quick-created from Business Case Builder. KPI: ${selectedKPI.name}.`,
          sections: [],
          solution_data: roi,
        }),
      });
      if (!saveRes.ok) throw new Error('save failed');
      const saved = await saveRes.json();

      toast.success('Business case created');
      onCreated?.(saved.id);
      onClose?.();
    } catch (e) {
      logger.error('inline-create submit failed:', e);
      setError('Could not create the case. Please try again.');
    } finally {
      setCalculating(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-start sm:items-center justify-center p-3 sm:p-6 overflow-y-auto"
      data-testid="inline-create-modal"
    >
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b bg-gradient-to-r from-teal-600 to-purple-700 text-white">
          <div className="flex items-center gap-2">
            <Calculator size={20} />
            <div>
              <h2 className="text-base font-semibold">Create a business case</h2>
              <p className="text-[11px] text-white/70">
                Model a KPI and learning interventions without leaving this page
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-white/20"
            data-testid="inline-create-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Stepper */}
        <div className="px-5 py-3 border-b bg-slate-50 flex items-center gap-2 text-xs font-medium text-slate-600">
          {['Pick KPI', 'Choose interventions', 'Confirm & calculate'].map((label, i) => (
            <div key={label} className="flex items-center gap-2">
              <span
                className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] ${
                  step >= i ? 'bg-teal-600 text-white' : 'bg-slate-200 text-slate-500'
                }`}
              >
                {i + 1}
              </span>
              <span className={step >= i ? 'text-slate-800' : 'text-slate-400'}>{label}</span>
              {i < 2 && <span className="text-slate-300">›</span>}
            </div>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {error && (
            <div className="mb-3 flex items-start gap-2 p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-sm">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {step === 0 && (
            <div data-testid="inline-create-step-kpi">
              <h3 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2">
                <Target size={16} /> Which KPI do you need to move?
              </h3>
              {loading ? (
                <div className="flex items-center justify-center py-8 text-slate-500 text-sm">
                  <Loader2 className="animate-spin mr-2" size={16} /> Loading KPIs…
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {kpis.map(kpi => (
                    <button
                      key={kpi.id}
                      onClick={() => handleKPI(kpi)}
                      className={`text-left p-3 rounded-lg border-2 transition-all ${
                        selectedKPI?.id === kpi.id
                          ? 'border-teal-600 bg-teal-50'
                          : 'border-slate-200 hover:border-teal-400 hover:bg-slate-50'
                      }`}
                      data-testid={`inline-create-kpi-${kpi.id}`}
                    >
                      <div className="text-sm font-semibold text-slate-900">{kpi.name}</div>
                      <div className="text-[11px] text-slate-500 mt-1">{kpi.issue_statement}</div>
                      <div className="text-[10px] text-slate-400 mt-1">{kpi.category}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 1 && selectedKPI && (
            <div data-testid="inline-create-step-interventions">
              <h3 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2">
                <Layers size={16} /> Pick the interventions that will move{' '}
                <span className="text-teal-700">{selectedKPI.name}</span>
              </h3>
              <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                {interventions.map(iv => {
                  const active = !!selected[iv.id];
                  return (
                    <button
                      key={iv.id}
                      onClick={() => toggleIntervention(iv)}
                      className={`w-full text-left p-3 rounded-lg border-2 transition-all flex items-start gap-3 ${
                        active
                          ? 'border-teal-600 bg-teal-50'
                          : 'border-slate-200 hover:border-teal-400 hover:bg-slate-50'
                      }`}
                      data-testid={`inline-create-iv-${iv.id}`}
                    >
                      <span
                        className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center text-white text-[10px] shrink-0 ${
                          active ? 'bg-teal-600 border-teal-600' : 'border-slate-300'
                        }`}
                      >
                        {active ? '✓' : ''}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-900">{iv.name}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">
                          {iv.description}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="inline-create-step-confirm">
              <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Users size={16} /> Confirm audience + modality per intervention
              </h3>
              <div className="space-y-2 mb-4">
                {Object.entries(selected).map(([ivId, cfg]) => {
                  const meta = interventions.find(i => i.id === ivId) || { name: ivId };
                  return (
                    <div
                      key={ivId}
                      className="border border-slate-200 rounded-lg p-3 bg-slate-50"
                      data-testid={`inline-create-cfg-${ivId}`}
                    >
                      <div className="text-sm font-semibold text-slate-900 mb-2">{meta.name}</div>
                      <div className="grid grid-cols-2 gap-2">
                        <label className="block text-[11px] text-slate-500">
                          Modality
                          <select
                            value={cfg.modality_id}
                            onChange={e => updateConfig(ivId, { modality_id: e.target.value })}
                            className="w-full mt-1 px-2 py-1.5 text-sm rounded border border-slate-200 bg-white"
                            data-testid={`inline-create-modality-${ivId}`}
                          >
                            {modalities.map(m => (
                              <option key={m.id} value={m.id}>
                                {m.name}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="block text-[11px] text-slate-500">
                          Audience size
                          <input
                            type="number"
                            min={1}
                            value={cfg.target_audience_size}
                            onChange={e =>
                              updateConfig(ivId, {
                                target_audience_size: Math.max(1, parseInt(e.target.value, 10) || 0),
                              })
                            }
                            className="w-full mt-1 px-2 py-1.5 text-sm rounded border border-slate-200 bg-white"
                            data-testid={`inline-create-size-${ivId}`}
                          />
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
              <label className="block text-xs text-slate-500 mb-1">Time horizon</label>
              <select
                value={timeHorizon}
                onChange={e => setTimeHorizon(parseInt(e.target.value, 10))}
                className="px-2 py-1.5 text-sm rounded border border-slate-200 bg-white"
                data-testid="inline-create-horizon"
              >
                <option value={6}>6 months</option>
                <option value={12}>12 months</option>
                <option value={24}>24 months</option>
              </select>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t bg-white flex items-center justify-between">
          <button
            onClick={() => setStep(s => Math.max(0, s - 1))}
            disabled={step === 0 || calculating}
            className="text-sm text-slate-500 hover:text-slate-800 disabled:opacity-40"
            data-testid="inline-create-back"
          >
            ← Back
          </button>
          {step < 2 ? (
            <button
              onClick={() => setStep(s => Math.min(2, s + 1))}
              disabled={
                (step === 0 && !selectedKPI) ||
                (step === 1 && !canProceedFromStep1)
              }
              className="px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="inline-create-next"
            >
              Next →
            </button>
          ) : (
            <button
              onClick={handleCalculateAndSave}
              disabled={!canSubmit || calculating}
              className="px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              data-testid="inline-create-submit"
            >
              {calculating ? (
                <>
                  <Loader2 className="animate-spin" size={14} /> Creating…
                </>
              ) : (
                <>
                  <Calculator size={14} /> Calculate &amp; Save
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default InlineCreateCaseModal;
